import {useCallback} from 'react';
import type {ColumnConfig} from 'schema/table/types';
import {useAppDispatch, useAppSelector} from 'store/hooks';
import type {SchemaTableConfig} from 'schema/contexts';
import type {FilterModel, PaginationModel, Sorting} from '../types';
import {
  selectSettingsTables,
  tableColumnConfigUpdated,
  tableFilterModelUpdated,
  tablePaginationModelResetted,
  tablePaginationModelUpdated,
  tableSortingUpdated,
} from '../settingsSlice';

/**
 * Reads and updates the persisted per-schema table configuration in the settings
 * slice. Supplied to the schema table layer via the SchemaDataAccess seam.
 */
export const useTableConfig = (schemaName: string): SchemaTableConfig => {
  const dispatch = useAppDispatch();
  const tablesState = useAppSelector(selectSettingsTables);
  const tableState = tablesState[schemaName];

  const setColumnConfigs = useCallback(
    (columnConfigs: ColumnConfig[]) => {
      dispatch(
        tableColumnConfigUpdated({name: schemaName, config: columnConfigs}),
      );
    },
    [schemaName, dispatch],
  );

  const setSortingConfig = useCallback(
    (sorting?: Sorting) => {
      dispatch(tableSortingUpdated({name: schemaName, sorting}));
    },
    [schemaName, dispatch],
  );

  const setFilterModel = useCallback(
    (filterModel?: FilterModel) => {
      dispatch(tableFilterModelUpdated({name: schemaName, filterModel}));
    },
    [schemaName, dispatch],
  );

  const setPaginationModel = useCallback(
    (paginationModel: PaginationModel) => {
      dispatch(
        tablePaginationModelUpdated({name: schemaName, paginationModel}),
      );
    },
    [schemaName, dispatch],
  );

  const resetPaginationModel = useCallback(
    (pageSize: number) => {
      dispatch(tablePaginationModelResetted({name: schemaName, pageSize}));
    },
    [schemaName, dispatch],
  );

  return {
    columnConfigs: tableState?.columns,
    sortingConfig: tableState?.sorting,
    filterModel: tableState?.filterModel ?? undefined,
    paginationModel: tableState?.paginationModel ?? undefined,
    setColumnConfigs,
    setSortingConfig,
    setFilterModel,
    setPaginationModel,
    resetPaginationModel,
  };
};
